import { z } from 'zod';

/**
 * Fundline claims in a Supabase access token, added by `public.custom_access_token_hook` (PLAN §3.12).
 * Used by the browser (to pick the shell and guard routes; RLS is the real enforcement) and by the worker
 * (after the token signature has been verified).
 */

export const WS_ROLES = ['admin', 'rep'] as const;
export type WsRole = (typeof WS_ROLES)[number];

const RawClaimsSchema = z.object({
  sub: z.uuid(),
  email: z.string().optional(),
  role: z.string().optional(),
  workspace_id: z.uuid().nullish(),
  ws_role: z.enum(WS_ROLES).nullish(),
  platform_owner: z.boolean().optional(),
  exp: z.number().optional(),
});

export interface AuthClaims {
  userId: string;
  email: string | null;
  /** Active workspace, or null (platform owner, pending invitation, disabled member). */
  workspaceId: string | null;
  /** Role in `workspaceId`; always null when `workspaceId` is null. */
  role: WsRole | null;
  platformOwner: boolean;
  /** Expiry, seconds since the epoch, when present. */
  expiresAt: number | null;
}

/** Parses a decoded JWT payload. Returns null when it is not a signed-in Supabase user token. */
export function parseClaims(payload: unknown): AuthClaims | null {
  const parsed = RawClaimsSchema.safeParse(payload);
  if (!parsed.success) return null;
  const c = parsed.data;
  if (c.role !== undefined && c.role !== 'authenticated') return null;
  const workspaceId = c.workspace_id ?? null;
  return {
    userId: c.sub,
    email: c.email ?? null,
    workspaceId,
    role: workspaceId ? (c.ws_role ?? null) : null,
    platformOwner: c.platform_owner ?? false,
    expiresAt: c.exp ?? null,
  };
}
